import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { JSONResponse, JSONResponseStatus } from 'src/app/core/models/json-response';
import { AuthService, InitiateUnlockResult } from 'src/app/core/services/auth.service';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { LoadingIndicatorDirective } from '../../../core/directives/loading-indicator.directive';

type UnlockState = 'form' | 'code-entry' | 'reset-password' | 'success' | 'error' | 'blocked';

@Component({
  selector: 'th-account-unlock',
  templateUrl: './account-unlock.component.html',
  styleUrl: './account-unlock.component.scss',
  imports: [FormsModule, LoadingIndicatorDirective]
})
export class AccountUnlockComponent implements AfterViewInit, OnDestroy {
  @Input() initialUsername = '';
  @Output() backToLogin = new EventEmitter<void>();

  username = '';
  maskedEmail = '';
  code = '';
  newPassword = '';
  confirmPassword = '';
  submitting = false;
  state: UnlockState = 'form';
  sendError = '';
  verifyError = '';
  passwordMismatch = false;
  resetError = '';
  showNewPassword = false;
  showConfirmPassword = false;
  showAlternative = false;

  private storedUsername = '';
  resendMessage = '';
  resendError = '';
  resendCooldown = 0;
  resending = false;
  private resendCooldownInterval: ReturnType<typeof setInterval> | null = null;

  get supportPhone(): string {
    return this.environmentService.supportPhone;
  }

  get supportEmail(): string {
    return this.environmentService.supportEmail;
  }

  get logoUrl(): string {
    return `assets/img/logo/${this.environmentService.logo}`;
  }

  constructor(
    private authService: AuthService,
    private environmentService: EnvironmentService
  ) {}

  ngAfterViewInit() {
    if (this.initialUsername) {
      this.username = this.initialUsername;
      this.onSubmit();
    }
  }

  ngOnDestroy() {
    this.newPassword = '';
    this.confirmPassword = '';
    this.storedUsername = '';
    if (this.resendCooldownInterval) {
      clearInterval(this.resendCooldownInterval);
      this.resendCooldownInterval = null;
    }
  }

  onSubmit() {
    if (this.submitting || !this.username.trim()) {
      return;
    }

    this.submitting = true;
    this.sendError = '';

    this.authService.initiateUnlock(this.username.trim()).subscribe({
      next: (response: JSONResponse<InitiateUnlockResult | string>) => {
        if (response.status === JSONResponseStatus.ERROR) {
          this.submitting = false;
          this.state = 'error';
          return;
        }
        if (response.status === JSONResponseStatus.VALIDATIONERROR) {
          this.submitting = false;
          this.sendError =
            typeof response.result === 'string'
              ? response.result
              : 'There was a problem sending the verification code. Please try again or contact support.';
          return;
        }
        if (response.status !== JSONResponseStatus.SUCCESS) {
          this.submitting = false;
          this.state = 'error';
          return;
        }
        const result = typeof response.result !== 'string' ? response.result : null;
        if (!result?.maskedEmail) {
          console.warn('[AccountUnlock] SUCCESS response missing maskedEmail; status:', response.status);
        }
        this.maskedEmail = result?.maskedEmail ?? '';
        this.storedUsername = this.username.trim();
        this.submitting = false;
        this.state = 'code-entry';
      },
      error: (err) => {
        console.error('[AccountUnlock] initiateUnlock failed; status:', err?.status);
        this.state = 'error';
        this.submitting = false;
      },
      complete: () => {
        this.submitting = false;
      }
    });
  }

  onVerify() {
    if (this.submitting || !this.code.trim()) {
      return;
    }

    this.submitting = true;
    this.verifyError = '';

    this.authService.verifyUnlock(this.code.trim()).subscribe({
      next: (response: JSONResponse<string>) => {
        if (response.status === JSONResponseStatus.VALIDATIONERROR) {
          this.submitting = false;
          this.verifyError =
            typeof response.result === 'string' && response.result
              ? response.result
              : 'Verification failed. Please try again or contact support.';
        } else if (response.status === JSONResponseStatus.ERROR) {
          this.submitting = false;
          this.state = 'blocked';
        } else if (response.status === JSONResponseStatus.SUCCESS) {
          this.submitting = false;
          this.state = 'reset-password';
        } else {
          this.submitting = false;
          this.state = 'error';
        }
      },
      error: (err) => {
        console.error('[AccountUnlock] verifyUnlock failed; status:', err?.status);
        this.state = 'error';
        this.submitting = false;
      },
      complete: () => {
        this.submitting = false;
      }
    });
  }

  onResendCode(): void {
    if (this.resending || this.resendCooldown > 0 || !this.storedUsername) return;
    this.resending = true;
    this.resendMessage = '';
    this.resendError = '';
    this.authService.initiateUnlock(this.storedUsername).subscribe({
      next: (response: JSONResponse<InitiateUnlockResult | string>) => {
        if (response.status === JSONResponseStatus.SUCCESS) {
          this.resending = false;
          this.resendMessage = 'A new verification code has been sent.';
          this.startResendCooldown(60);
          return;
        }
        if (response.status === JSONResponseStatus.VALIDATIONERROR) {
          this.resending = false;
          this.resendError =
            typeof response.result === 'string' && response.result
              ? response.result
              : 'Unable to resend code. Please try again.';
          return;
        }
        if (response.status === JSONResponseStatus.ERROR) {
          this.resending = false;
          this.state = 'error';
          return;
        }
        this.resending = false;
        this.resendError = 'Unable to resend code. Please try again.';
      },
      error: () => {
        this.resending = false;
        this.resendError = 'Unable to resend code. Please try again.';
      },
      complete: () => {
        this.resending = false;
      }
    });
  }

  private startResendCooldown(seconds: number): void {
    if (this.resendCooldownInterval) {
      clearInterval(this.resendCooldownInterval);
    }
    this.resendCooldown = seconds;
    this.resendCooldownInterval = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        clearInterval(this.resendCooldownInterval!);
        this.resendCooldownInterval = null;
      }
    }, 1000);
  }

  private validatePasswordStrength(password: string): string {
    if (password.length < 8) {
      return 'Password must be at least 8 characters.';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain a lowercase letter.';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain an uppercase letter.';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain a number.';
    }
    return '';
  }

  onResetPassword() {
    if (this.submitting) {
      return;
    }

    this.passwordMismatch = false;
    this.resetError = '';

    if (!this.newPassword || !this.confirmPassword) {
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.passwordMismatch = true;
      return;
    }

    const strengthError = this.validatePasswordStrength(this.newPassword);
    if (strengthError) {
      this.resetError = strengthError;
      return;
    }

    this.submitting = true;

    this.authService.resetUnlockPassword(this.newPassword).subscribe({
      next: (response: JSONResponse<string>) => {
        if (response.status === JSONResponseStatus.VALIDATIONERROR) {
          this.submitting = false;
          this.resetError =
            typeof response.result === 'string'
              ? response.result
              : 'There was a problem resetting your password. Please try again or contact support.';
        } else if (response.status === JSONResponseStatus.SUCCESS) {
          this.newPassword = '';
          this.confirmPassword = '';
          this.submitting = false;
          this.state = 'success';
        } else {
          this.newPassword = '';
          this.confirmPassword = '';
          this.submitting = false;
          this.state = 'error';
        }
      },
      error: (err) => {
        console.error('[AccountUnlock] resetUnlockPassword failed; status:', err?.status);
        this.newPassword = '';
        this.confirmPassword = '';
        this.state = 'error';
        this.submitting = false;
      },
      complete: () => {
        this.submitting = false;
      }
    });
  }
}
